import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Tooltip,
  Alert,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Restore as RestoreIcon,
  DeleteForever as DeleteForeverIcon,
  Search as SearchIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiService, TrashItem, extractApiErrorMessage } from '../services/apiService';
import { useProjects } from '../context/ProjectContext';

type TrashDisplayRow = {
  item: TrashItem;
  depth: number;
  showActions: boolean;
};

const trashRowKey = (item: TrashItem) => `${item.type}-${item.id}`;

/** Parent collection rows first; sub-collections nested underneath (actions on parent only). */
function buildTrashDisplayRows(items: TrashItem[]): TrashDisplayRow[] {
  const collectionItems = items.filter((i) => i.type === 'collection');
  const otherItems = items.filter((i) => i.type !== 'collection');
  const collectionIdsInTrash = new Set(collectionItems.map((c) => c.id));

  const childrenByParent = new Map<number, TrashItem[]>();
  for (const c of collectionItems) {
    const pid = c.parentCollectionId;
    if (pid != null && collectionIdsInTrash.has(pid)) {
      const list = childrenByParent.get(pid) ?? [];
      list.push(c);
      childrenByParent.set(pid, list);
    }
  }

  const roots = collectionItems.filter(
    (c) => c.parentCollectionId == null || !collectionIdsInTrash.has(c.parentCollectionId)
  );
  const sortByName = (a: TrashItem, b: TrashItem) => a.name.localeCompare(b.name);
  roots.sort(sortByName);

  const rows: TrashDisplayRow[] = [];
  const appendSubtree = (item: TrashItem, depth: number, showActions: boolean) => {
    rows.push({ item, depth, showActions });
    const kids = (childrenByParent.get(item.id) ?? []).slice().sort(sortByName);
    for (const child of kids) {
      appendSubtree(child, depth + 1, false);
    }
  };
  for (const root of roots) {
    appendSubtree(root, 0, true);
  }
  for (const other of otherItems) {
    rows.push({ item: other, depth: 0, showActions: true });
  }
  return rows;
}

function collectDescendantCollectionIds(
  parentId: number,
  collections: TrashItem[]
): number[] {
  const ids: number[] = [];
  const queue = [parentId];
  while (queue.length > 0) {
    const pid = queue.shift()!;
    for (const c of collections) {
      if (c.type === 'collection' && c.parentCollectionId === pid) {
        ids.push(c.id);
        queue.push(c.id);
      }
    }
  }
  return ids;
}

const Trash: React.FC = () => {
  const navigate = useNavigate();
  const { refreshProjects, projects, setSelectedProject } = useProjects();
  const [trashItems, setTrashItems] = useState<TrashItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<TrashItem[]>([]);
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'info' | 'error' } | null>(null);
  const [permanentDeleteDialogOpen, setPermanentDeleteDialogOpen] = useState(false);
  const [itemToPermanentDelete, setItemToPermanentDelete] = useState<TrashItem | null>(null);
  const [permanentDeleteConfirmText, setPermanentDeleteConfirmText] = useState('');
  const [permanentDeleteBlockedMessage, setPermanentDeleteBlockedMessage] = useState<string | null>(null);

  const removeItemsFromTrash = useCallback((keysToRemove: Set<string>) => {
    const withoutRemoved = (list: TrashItem[]) =>
      list.filter((item) => !keysToRemove.has(trashRowKey(item)));
    setTrashItems((prev) => withoutRemoved(prev));
    setFilteredItems((prev) => withoutRemoved(prev));
  }, []);

  const keysToRemoveForItem = useCallback(
    (item: TrashItem, allItems: TrashItem[]) => {
      const keysToRemove = new Set([trashRowKey(item)]);
      if (item.type === 'collection') {
        const descendantIds = collectDescendantCollectionIds(
          item.id,
          allItems.filter((i) => i.type === 'collection')
        );
        for (const did of descendantIds) {
          keysToRemove.add(trashRowKey({ ...item, id: did, type: 'collection' }));
        }
      }
      return keysToRemove;
    },
    []
  );

  const fetchTrash = useCallback(async (options?: { silent?: boolean }) => {
    if (!options?.silent) {
      setLoading(true);
    }
    setError(null);
    try {
      const data = await apiService.getTrash();
      setTrashItems(data);
      setFilteredItems(data);
    } catch (err) {
      setError('Failed to load recycle bin');
      console.error('Error loading trash:', err);
    } finally {
      if (!options?.silent) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchTrash();
  }, [fetchTrash]);

  useEffect(() => {
    if (!searchText.trim()) {
      setFilteredItems(trashItems);
      return;
    }
    const q = searchText.toLowerCase();
    const matches = (item: TrashItem) =>
      item.name.toLowerCase().includes(q) ||
      item.workspace.toLowerCase().includes(q) ||
      item.deletedBy.toLowerCase().includes(q);

    const matching = trashItems.filter(matches);
    const visibleKeys = new Set(matching.map(trashRowKey));
    const collections = trashItems.filter((i) => i.type === 'collection');
    const collectionById = new Map(collections.map((c) => [c.id, c]));

    for (const item of matching) {
      if (item.type !== 'collection') continue;
      let pid = item.parentCollectionId ?? null;
      while (pid != null) {
        const parent = collectionById.get(pid);
        if (!parent) break;
        visibleKeys.add(trashRowKey(parent));
        pid = parent.parentCollectionId ?? null;
      }
      for (const child of collections) {
        if (child.parentCollectionId === item.id) {
          visibleKeys.add(trashRowKey(child));
        }
      }
    }

    setFilteredItems(trashItems.filter((item) => visibleKeys.has(trashRowKey(item))));
  }, [searchText, trashItems]);

  const displayRows = useMemo(
    () => buildTrashDisplayRows(filteredItems),
    [filteredItems]
  );

  const handleRestore = async (item: TrashItem) => {
    try {
      let shouldRefreshProjects = false;
      if (item.type === 'collection') {
        await apiService.restoreCollection(item.id);
        setMessage({ text: `"${item.name}" restored to Collections`, type: 'success' });
      } else if (item.type === 'project') {
        await apiService.restoreProject(item.id);
        setMessage({ text: `"${item.name}" project restored`, type: 'success' });
        shouldRefreshProjects = true;
      } else if (item.type === 'environment') {
        await apiService.restoreEnvironment(item.id);
        setMessage({ text: `"${item.name}" environment restored`, type: 'success' });
      }
      removeItemsFromTrash(keysToRemoveForItem(item, trashItems));
      if (shouldRefreshProjects) {
        await refreshProjects();
      }
    } catch (err: any) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Failed to restore item';
      setError(typeof msg === 'string' ? msg : 'Failed to restore item');
      console.error('Error restoring:', err);
    }
  };

  const openPermanentDeleteDialog = (item: TrashItem) => {
    setItemToPermanentDelete(item);
    setPermanentDeleteConfirmText('');
    setPermanentDeleteBlockedMessage(null);
    setPermanentDeleteDialogOpen(true);
  };

  const closePermanentDeleteDialog = () => {
    setPermanentDeleteDialogOpen(false);
    setItemToPermanentDelete(null);
    setPermanentDeleteConfirmText('');
    setPermanentDeleteBlockedMessage(null);
  };

  const handleRestoreAndManageVariables = async () => {
    if (!itemToPermanentDelete || itemToPermanentDelete.type !== 'environment') return;
    try {
      await apiService.restoreEnvironment(itemToPermanentDelete.id);
      const matchingProject = projects.find((p) => p.name === itemToPermanentDelete.workspace);
      if (matchingProject) {
        setSelectedProject(matchingProject);
      }
      removeItemsFromTrash(keysToRemoveForItem(itemToPermanentDelete, trashItems));
      closePermanentDeleteDialog();
      navigate('/environments');
    } catch (err) {
      const msg = extractApiErrorMessage(err) ?? 'Failed to restore environment';
      setError(msg);
      console.error('Error restoring environment:', err);
    }
  };

  const handlePermanentDelete = async () => {
    if (!itemToPermanentDelete) return;
    try {
      let shouldRefreshProjects = false;
      if (itemToPermanentDelete.type === 'collection') {
        await apiService.permanentDeleteCollection(itemToPermanentDelete.id);
      } else if (itemToPermanentDelete.type === 'project') {
        setError('Project deletion is not allowed');
        closePermanentDeleteDialog();
        return;
      } else if (itemToPermanentDelete.type === 'environment') {
        await apiService.permanentDeleteEnvironment(itemToPermanentDelete.id);
      }
      if (shouldRefreshProjects) {
        await refreshProjects();
      }
      setMessage({ text: `"${itemToPermanentDelete.name}" permanently deleted`, type: 'success' });
      removeItemsFromTrash(keysToRemoveForItem(itemToPermanentDelete, trashItems));
      closePermanentDeleteDialog();
    } catch (err) {
      const message = extractApiErrorMessage(err) ?? 'Failed to permanently delete item';
      if (message.toLowerCase().includes('cannot delete environment')) {
        setPermanentDeleteBlockedMessage(message);
      } else {
        setError(message);
        closePermanentDeleteDialog();
      }
      console.error('Error permanent delete:', err);
    }
  };

  const formatDate = (dateStr: string) => {
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr: string, epochMs?: number) => {
    try {
      const d = new Date(typeof epochMs === 'number' ? epochMs : dateStr);
      if (Number.isNaN(d.getTime())) return dateStr;
      const date = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
      const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
      return `${date}, ${time}`;
    } catch {
      return dateStr;
    }
  };

  /** Human-readable countdown until automatic purge (dayOfRemoval = deleted_at + 24h from server). */
  const formatRemovalCountdown = (item: TrashItem) => {
    try {
      const removalMs =
        typeof item.dayOfRemovalEpochMs === 'number'
          ? item.dayOfRemovalEpochMs
          : new Date(item.dayOfRemoval).getTime();
      if (Number.isNaN(removalMs)) return '';

      const removal = new Date(removalMs);
      const msLeft =
        typeof item.msUntilRemoval === 'number'
          ? item.msUntilRemoval
          : removalMs - Date.now();

      if (msLeft <= 0) {
        return 'Removes soon (pending automatic cleanup)';
      }

      const hoursLeft = Math.floor(msLeft / (1000 * 60 * 60));
      const minutesLeft = Math.floor((msLeft % (1000 * 60 * 60)) / (1000 * 60));
      const timeStr = removal.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });

      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const isToday = removal.toDateString() === today.toDateString();
      const isTomorrow = removal.toDateString() === tomorrow.toDateString();

      const durationParts: string[] = [];
      if (hoursLeft > 0) durationParts.push(`${hoursLeft} hr${hoursLeft === 1 ? '' : 's'}`);
      if (minutesLeft > 0 || hoursLeft === 0) durationParts.push(`${minutesLeft} min`);
      const durationLeft = durationParts.join(' ');

      if (msLeft < 60 * 60 * 1000) {
        return minutesLeft > 0 ? `Less than 1 hour left (${durationLeft})` : 'Removes soon';
      }

      if (isToday) {
        return `Removes today at ${timeStr} (${durationLeft} left)`;
      }
      if (isTomorrow) {
        return `Removes tomorrow at ${timeStr} (${durationLeft} left)`;
      }
      return `Removes ${formatDate(item.dayOfRemoval)} at ${timeStr} (${durationLeft} left)`;
    } catch {
      return '';
    }
  };

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', width: '100%', minWidth: 0, maxWidth: '100%', minHeight: 'calc(100vh - 112px)', boxSizing: 'border-box' }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 2, textTransform: 'none', alignSelf: 'flex-start' }}
      >
        Back
      </Button>

      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
        Recycle Bin
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
        Deleted collections and Environments from workspaces you have access to will appear here. You can
        restore or permanently delete collections if you have edit permissions.
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
        <InfoIcon fontSize="small" color="action" />
        <Typography variant="body2" color="text.secondary">
          Collections are removed from the recycle bin after 1 day based on server time (not your local system date/time).
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {message && (
        <Alert
          severity={message.type as 'success' | 'info' | 'error'}
          sx={{ mb: 2 }}
          onClose={() => setMessage(null)}
        >
          {message.text}
        </Alert>
      )}

      <TextField
        fullWidth
        size="small"
        placeholder="Search collections"
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" color="action" />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2, flexShrink: 0 }}
      />

      {loading ? (
        <Typography sx={{ flex: 1 }}>Loading...</Typography>
      ) : displayRows.length === 0 ? (
        <Typography color="text.secondary" sx={{ flex: 1 }}>No deleted collections in recycle bin.</Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ flex: 1, width: '100%', minWidth: 0, maxWidth: '100%', minHeight: 0, overflow: 'auto', display: 'block', boxSizing: 'border-box' }}>
          <Table size="small" sx={{ width: '100%', minWidth: '100%', tableLayout: 'fixed', display: 'table' }}>
            <colgroup>
              <col style={{ width: '16%' }} />
              <col style={{ width: '18%' }} />
              <col style={{ width: '18%' }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: '20%' }} />
              <col style={{ width: '14%' }} />
            </colgroup>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Workspace</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Deleted by</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Deleted on</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Day of removal</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayRows.map(({ item, depth, showActions }) => (
                <TableRow key={trashRowKey(item)} hover>
                  <TableCell>
                    <Box sx={{ pl: depth * 3, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {depth > 0 && (
                        <Typography component="span" variant="body2" color="text.secondary">
                          └
                        </Typography>
                      )}
                      <span>{item.name}</span>
                    </Box>
                  </TableCell>
                  <TableCell>{item.workspace}</TableCell>
                  <TableCell>{item.deletedBy}</TableCell>
                  <TableCell>{formatDate(item.deletedOn)}</TableCell>
                  <TableCell>
                    {formatDateTime(item.dayOfRemoval, item.dayOfRemovalEpochMs)}
                    <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                      {formatRemovalCountdown(item)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {showActions ? (
                      <>
                        <Tooltip title="Restore">
                          <IconButton size="small" onClick={() => handleRestore(item)} color="primary">
                            <RestoreIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {item.type !== 'project' && (
                          <Tooltip title="Delete permanently">
                            <IconButton
                              size="small"
                              onClick={() => openPermanentDeleteDialog(item)}
                              color="error"
                            >
                              <DeleteForeverIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Permanent Delete Confirmation Dialog */}
      <Dialog
        open={permanentDeleteDialogOpen}
        onClose={closePermanentDeleteDialog}
      >
        {permanentDeleteBlockedMessage ? (
          <>
            <DialogTitle>Cannot Delete Environment</DialogTitle>
            <DialogContent>
              <DialogContentText>{permanentDeleteBlockedMessage}</DialogContentText>
            </DialogContent>
            <DialogActions>
              {itemToPermanentDelete?.type === 'environment' &&
              permanentDeleteBlockedMessage.toLowerCase().includes('project-scoped') ? (
                <Button onClick={handleRestoreAndManageVariables} variant="outlined">
                  Restore &amp; manage variables
                </Button>
              ) : null}
              <Button onClick={closePermanentDeleteDialog} variant="contained">
                OK
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogTitle>Delete &quot;{itemToPermanentDelete?.name}&quot; permanently?</DialogTitle>
            <DialogContent>
              <Typography sx={{ mb: 2 }}>
                {itemToPermanentDelete?.type === 'environment'
                  ? 'This action cannot be undone. The environment will be permanently removed.'
                  : 'This action cannot be undone. The collection and all its requests will be permanently removed.'}
              </Typography>
              <Typography sx={{ mb: 1 }}>
                To confirm, type &quot;delete&quot; in the box below.
              </Typography>
              <TextField
                fullWidth
                placeholder="Type delete"
                value={permanentDeleteConfirmText}
                onChange={(e) => setPermanentDeleteConfirmText(e.target.value)}
                size="small"
                sx={{ mt: 1 }}
                error={permanentDeleteConfirmText.length > 0 && permanentDeleteConfirmText !== 'delete'}
                helperText={
                  permanentDeleteConfirmText.length > 0 && permanentDeleteConfirmText !== 'delete'
                    ? 'Enter exactly "delete" with no spaces or extra characters.'
                    : undefined
                }
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={closePermanentDeleteDialog}>Cancel</Button>
              <Button
                onClick={handlePermanentDelete}
                color="error"
                variant="contained"
                disabled={permanentDeleteConfirmText !== 'delete'}
              >
                Delete Permanently
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default Trash;
